import { Component, OnChanges, inject, ChangeDetectionStrategy, input, output } from '@angular/core';
import { ModelService } from '../../../services/model-service';
import { GuruEntity } from '../../../model/guru-entity';
import { SelectionUtils } from '../../selection-utils';
import { UnitConversionUtils } from '../../unit-conversion-utils';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-performance-input',
  templateUrl: './performance-input.component.html',
  styleUrls: ['./performance-input.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
})
export class PerformanceInputComponent implements OnChanges {
  private modelService = inject(ModelService);
  readonly engineKey = input.required<string>();
  readonly selectionEntities = input.required<GuruEntity[]>();
  readonly isMetric = input(false);
  readonly isSureflow = input<boolean>(undefined!);
  readonly valueChanged = output<void>();
  deltaSelections: string[] = ['Temperature Change', 'Fluid Flow Rate'];
  coolingDeltaValue!: string;
  coolingEnteringDryBulbSelection!: GuruEntity;
  coolingEnteringWetBulbSelection!: GuruEntity;
  coolingEnteringFluidTemperatureSelection!: GuruEntity;
  coolingFluidTypeSelection!: GuruEntity;
  coolingGlycolPercentageSelection!: GuruEntity;
  selectedCoolingDelta!: string;
  heatingDeltaValue!: string;
  heatingEnteringDryBulbSelection!: GuruEntity;
  heatingEnteringFluidTemperatureSelection!: GuruEntity;
  heatingFluidTypeSelection!: GuruEntity;
  heatingGlycolPercentageSelection!: GuruEntity;
  selectedHeatingDelta!: string;

  ngOnChanges(): void {
    this.coolingEnteringDryBulbSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USCEDB');
    this.coolingEnteringWetBulbSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USCEWB');
    this.coolingEnteringFluidTemperatureSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USCEWT');
    this.coolingFluidTypeSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USCFluid');
    this.coolingGlycolPercentageSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USCFluidMix');
    const coolingTemperatureChange = this.getNumericSelectionValue('USCWTC');
    if (coolingTemperatureChange > 0) {
      this.coolingDeltaValue = coolingTemperatureChange.toString();
      this.selectedCoolingDelta = 'Temperature Change';
    } else {
      this.coolingDeltaValue = this.getNumericSelectionValue('USCWFR').toString();
      this.selectedCoolingDelta = 'Fluid Flow Rate';
    }
    this.heatingEnteringDryBulbSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USHEDB');
    this.heatingEnteringFluidTemperatureSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USHEWT');
    this.heatingFluidTypeSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USHFluid');
    this.heatingGlycolPercentageSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USHFluidMix');
    const heatingTemperatureChange = this.getNumericSelectionValue('USHWTC');
    if (heatingTemperatureChange > 0) {
      this.heatingDeltaValue = heatingTemperatureChange.toString();
      this.selectedHeatingDelta = 'Temperature Change';
    } else {
      this.heatingDeltaValue = this.getNumericSelectionValue('USHWFR').toString();
      this.selectedHeatingDelta = 'Fluid Flow Rate';
    }
  }

  private getNumericSelectionValue(entityName: string): number {
    const filtered = this.selectionEntities().filter((selection) => selection.Name === entityName);
    if (filtered && filtered.length === 1) {
      const entity = filtered[0];
      const value = +entity.Value.toString();
      if (!isNaN(value)) {
        return value;
      }
    }
    return 0;
  }

  deltaSelectionChanged(isCooling: boolean, newValue: string): void {
    this.setSelectedDelta(isCooling, newValue);
    let defaultValue: string;
    if (!this.isTemperatureChange(isCooling)) {
      defaultValue = this.isMetric() ? '0.2' : '3';
    } else if (isCooling) {
      defaultValue = this.isMetric() ? '5.6' : '10';
    } else {
      defaultValue = this.isMetric() ? '17' : '30';
    }
    this.deltaValueChanged(isCooling, defaultValue);
  }

  private setSelectedDelta(isCooling: boolean, newValue: string): void {
    if (isCooling) {
      this.selectedCoolingDelta = newValue;
    } else {
      this.selectedHeatingDelta = newValue;
    }
  }

  private isTemperatureChange(isCooling: boolean): boolean {
    const temperatureValue = 'Temperature Change';
    return isCooling ? temperatureValue === this.selectedCoolingDelta : temperatureValue === this.selectedHeatingDelta;
  }

  deltaValueChanged(isCooling: boolean, newValue: string): void {
    let entityName: string;
    let otherEntityName: string;
    if (this.isTemperatureChange(isCooling)) {
      entityName = isCooling ? 'USCWTC' : 'USHWTC';
      otherEntityName = isCooling ? 'USCWFR' : 'USHWFR';
    } else {
      entityName = isCooling ? 'USCWFR' : 'USHWFR';
      otherEntityName = isCooling ? 'USCWTC' : 'USHWTC';
    }
    this.modelService.setValue(entityName, this.engineKey(), newValue).subscribe(() => {
      this.modelService.setValue(otherEntityName, this.engineKey(), '0').subscribe(() => {
        this.valueChanged.emit();
      });
    });
  }

  selectionChanged(entity: GuruEntity, newValue: string): void {
    this.modelService.setValue(entity.Name, this.engineKey(), newValue).subscribe(() => this.valueChanged.emit());
  }

  getCoolingDeltaUnits(): string {
    return this.isTemperatureChange(true) ? UnitConversionUtils.getTemperatureUnits(this.isMetric()) : UnitConversionUtils.getFlowRateUnits(this.isMetric());
  }

  getHeatingDeltaUnits(): string {
    return this.isTemperatureChange(false) ? UnitConversionUtils.getTemperatureUnits(this.isMetric()) : UnitConversionUtils.getFlowRateUnits(this.isMetric());
  }

  getTemperatureUnits(): string {
    return UnitConversionUtils.getTemperatureUnits(this.isMetric());
  }
}
